import { AiUserSubscription, BillingCycle } from '../../types'

type Environment = 'dev' | 'prod'
type SubscriptionType = 'CONVERTER' | 'CREATOR' | 'COMPOSER'
type ValidProductIDKey = `${SubscriptionType}_PRODUCT`
type ValidPriceIDKey = `${SubscriptionType}_PRICE${string}`
type RequiredPriceIDKeys = keyof typeof ProdMonthlyPriceIDs
type RequiredProductIDKeys = keyof typeof ProdMonthlyProductIDs

type ProductIDRecord = Record<ValidProductIDKey, string>
type PriceIDRecord = Record<ValidPriceIDKey, string>

type PriceIdObject =
  | typeof ProdMonthlyPriceIDs
  | typeof DevMonthlyPriceIDs
  | typeof ProdAnnualPriceIDs
  | typeof DevAnnualPriceIDs

// Helper type to verify matching keys

export type PriceType =
  (typeof PriceIDMap)[Environment][BillingCycle][keyof typeof ProdMonthlyPriceIDs]

export const MAX_TRIAL_TRAINING_JOBS = 5

export const CREATOR_UNLIMITED_DOWNLOAD_CUTOFF_DATE = '2024-07-11T16:30:00+00:00'
export const CREATOR_75_CONVERTER_10_DOWNLOAD_CUTOFF_DATE = '2024-08-28T19:00:00+00:00'
export const CONVERTER_10_MIN_DOWNLOAD_CUTOFF_DATE = '2024-09-26T18:30:00+00:00'
export const CONVERTER_15_DOLLAR_CUTOFF_DATE = '2024-10-16T21:30:00+00:00'
export const BRAZILIAN_PRICING_CUTOFF_DATE = '2024-11-12T19:00:00+00:00'
export const BRAZILIAN_PRICING_SUNSET_CUTOFF_DATE = '2024-12-10T17:00:00+00:00'

/**
 * How to add a new price:
 * 1. Add the price to the Stripe dashboard, likely both a monthly and annual price
 * 2. Add the prices to the corresponding objects down below. The object value without a postfix will be considered
 * the default price.
 * 3. Update any Tier objects that reference the price, updating their `monthlyPrice` and `annualPrice` parameters
 * along with the `getPriceId` function as well. Remember that some tiers extend other tiers, so you may now need
 * to specify the monthlyPrice, annualPrice, and getPriceId functions for those.
 * 4. Once the new price goes live to prod, make it default if it will now be the new default price.
 */

export const ProdMonthlyProductIDs = {
  CONVERTER_PRODUCT: 'prod_OafFc0BVdW6BvZ',
  CREATOR_PRODUCT: 'prod_O2Y2bpUBKnSzPt',
  COMPOSER_PRODUCT: 'prod_Opy7VttZWTSKGJ'
} as const satisfies ProductIDRecord

export const ProdMonthlyPriceIDs = {
  CONVERTER_PRICE: 'price_1QAYWcLQMmLXvN01I9m80pAa', // $12 price
  CONVERTER_PRICE_10: 'price_1NnTv0LQMmLXvN01U8xk4wUW', // $10 price
  CONVERTER_PRICE_15: 'price_1Q2zLQLQMmLXvN01hQ4YjGom', // $15 price
  CONVERTER_PRICE_BRAZIL: 'price_1QHqv3LQMmLXvN01xb5olGGZ', // R$30 price
  CREATOR_PRICE: 'price_1NnTvXLQMmLXvN01ROCCcQuY',
  CREATOR_PRICE_BRAZIL: 'price_1QHqx2LQMmLXvN01mNr0cssu', // R$62.5 price
  COMPOSER_PRICE: 'price_1O2IBXLQMmLXvN01sa99Aj4y',
  COMPOSER_PRICE_BRAZIL: 'price_1QHqyqLQMmLXvN01dCUF1ZbZ' // R$150 price
} as const satisfies PriceIDRecord

export const ProdAnnualProductIDs = {
  CONVERTER_PRODUCT: 'prod_QcTJ7GKhMTLrQw',
  CREATOR_PRODUCT: 'prod_QcTJRs2CHmUJoZ',
  COMPOSER_PRODUCT: 'prod_QcTJTaSbwhWdO0'
} as const satisfies ProductIDRecord & Record<RequiredProductIDKeys, string>

export const ProdAnnualPriceIDs = {
  CONVERTER_PRICE: 'price_1QAYWpLQMmLXvN01a62g4h7R', // $12 price
  CONVERTER_PRICE_10: 'price_1PlENDLQMmLXvN01RKJa5f0T', // $10 price
  CONVERTER_PRICE_15: 'price_1Q2zKxLQMmLXvN01rg1DJ2XC', // $15 price
  CONVERTER_PRICE_BRAZIL: 'price_1QHqvoLQMmLXvN01dNNR4mhG', // R$288 price
  CREATOR_PRICE: 'price_1PlENFLQMmLXvN01Ohsncxvk',
  CREATOR_PRICE_BRAZIL: 'price_1QHqxzLQMmLXvN01m8jkmtu3', // R$600 price
  COMPOSER_PRICE: 'price_1PlENALQMmLXvN01n1hDLSSx',
  COMPOSER_PRICE_BRAZIL: 'price_1QHqzlLQMmLXvN01Yyt514Y0' // R$1440 price
} as const satisfies PriceIDRecord & Record<RequiredPriceIDKeys, string>

export const DevMonthlyProductIDs = {
  CONVERTER_PRODUCT: 'prod_Oaf5gGeVLaXyjR',
  CREATOR_PRODUCT: 'prod_O2XKXSohIP8rA5',
  COMPOSER_PRODUCT: 'prod_OpyDu8uwLt5zVm'
} as const satisfies ProductIDRecord & Record<RequiredProductIDKeys, string>

export const DevMonthlyPriceIDs = {
  CONVERTER_PRICE: 'price_1QAYVTLQMmLXvN01uwzWz5VV', // $12 price
  CONVERTER_PRICE_10: 'price_1NnTlTLQMmLXvN018ztBNMaY', // $10 price
  CONVERTER_PRICE_15: 'price_1Q2chWLQMmLXvN01sJYaAF5M', // $15 price
  CONVERTER_PRICE_BRAZIL: 'price_1QHqkRLQMmLXvN01RhbC6s0i', // R$30 price
  CREATOR_PRICE: 'price_1Nik9xLQMmLXvN01sX7gFP05',
  CREATOR_PRICE_BRAZIL: 'price_1QHqr4LQMmLXvN013ua7iAhG', // R$62.5 price
  COMPOSER_PRICE: 'price_1O2IGcLQMmLXvN01YR4e3zVV',
  COMPOSER_PRICE_BRAZIL: 'price_1QHqsvLQMmLXvN01Xae2Yxod' // R$150 price
} as const satisfies PriceIDRecord & Record<RequiredPriceIDKeys, string>

export const DevAnnualProductIDs = {
  CONVERTER_PRODUCT: 'prod_QWts8NL9GJJkzr',
  CREATOR_PRODUCT: 'prod_QWtrYO7Adrux92',
  COMPOSER_PRODUCT: 'prod_QWttrVadXXXdA6'
} as const satisfies ProductIDRecord & Record<RequiredProductIDKeys, string>

export const DevAnnualPriceIDs = {
  CONVERTER_PRICE: 'price_1QAYVxLQMmLXvN01ZvyOk22h', // $12 price
  CONVERTER_PRICE_10: 'price_1Pfq56LQMmLXvN01sYztlzG4', // $10 price
  CONVERTER_PRICE_15: 'price_1Q2dNULQMmLXvN01MncRujkV', // $15 price
  CONVERTER_PRICE_BRAZIL: 'price_1QHsiXLQMmLXvN01XwkTpkOt', // R$288 price
  CREATOR_PRICE: 'price_1Pfq4ELQMmLXvN012UBpcMWs',
  CREATOR_PRICE_BRAZIL: 'price_1QHsiyLQMmLXvN011Hh5AEeJ', // R$600 price
  COMPOSER_PRICE: 'price_1Pfq6CLQMmLXvN01205Dk0Bx',
  COMPOSER_PRICE_BRAZIL: 'price_1QHshqLQMmLXvN01J0qEVThU' // R$1440 price
} as const satisfies PriceIDRecord & Record<RequiredPriceIDKeys, string>

export const PriceIDMap = {
  dev: {
    [BillingCycle.MONTHLY]: DevMonthlyPriceIDs,
    [BillingCycle.ANNUAL]: DevAnnualPriceIDs
  },
  prod: {
    [BillingCycle.MONTHLY]: ProdMonthlyPriceIDs,
    [BillingCycle.ANNUAL]: ProdAnnualPriceIDs
  }
}

const getSubscriptionPriceIds = (priceIds: PriceIdObject, subscriptionType: SubscriptionType) => {
  const keys = Object.keys(priceIds) as Array<keyof PriceIdObject>
  return keys.filter((key) => key.startsWith(subscriptionType)).map((key) => priceIds[key])
}

/** Generates the product and prices object for a given environment and subscription level */
const createSubscriptionIds = (environment: Environment, billingCycle: BillingCycle) => {
  const productIds =
    environment === 'prod'
      ? billingCycle === BillingCycle.MONTHLY
        ? ProdMonthlyProductIDs
        : ProdAnnualProductIDs
      : billingCycle === BillingCycle.MONTHLY
        ? DevMonthlyProductIDs
        : DevAnnualProductIDs

  const priceIds =
    environment === 'prod'
      ? billingCycle === BillingCycle.MONTHLY
        ? ProdMonthlyPriceIDs
        : ProdAnnualPriceIDs
      : billingCycle === BillingCycle.MONTHLY
        ? DevMonthlyPriceIDs
        : DevAnnualPriceIDs

  return {
    PRODUCT: {
      [AiUserSubscription.FREE]: [null],
      [AiUserSubscription.CONVERTER]: [productIds.CONVERTER_PRODUCT],
      [AiUserSubscription.CREATOR]: [productIds.CREATOR_PRODUCT],
      [AiUserSubscription.COMPOSER]: [productIds.COMPOSER_PRODUCT]
    },
    PRICE: {
      [AiUserSubscription.FREE]: [null],
      [AiUserSubscription.CONVERTER]: getSubscriptionPriceIds(priceIds, 'CONVERTER'),
      [AiUserSubscription.CREATOR]: getSubscriptionPriceIds(priceIds, 'CREATOR'),
      [AiUserSubscription.COMPOSER]: getSubscriptionPriceIds(priceIds, 'COMPOSER')
    }
  }
}

/** The Stripe product and price IDs for the monthly subscriptions in production. */
export const PROD_MONTHLY_SUBSCRIPTION_IDS = createSubscriptionIds('prod', BillingCycle.MONTHLY)

/** The Stripe product and price IDs for the annual subscriptions in production. */
export const PROD_ANNUAL_SUBSCRIPTION_IDS = createSubscriptionIds('prod', BillingCycle.ANNUAL)

/** The Stripe product and price IDs for the monthly subscriptions in development. */
export const DEV_MONTHLY_SUBSCRIPTION_IDS = createSubscriptionIds('dev', BillingCycle.MONTHLY)

/** The Stripe product and price IDs for the annual subscriptions in development. */
export const DEV_ANNUAL_SUBSCRIPTION_IDS = createSubscriptionIds('dev', BillingCycle.ANNUAL)
